import { supabase } from "@/integrations/supabase/client";
import { TokenService } from "@/services/auth/TokenService";
import type {
  AgentCreationLog,
  AgentCreationLogDetail,
  AgentCreationLogFilters,
  AgentCreationLogsResponse,
  AgentCreationLogDetailsResponse,
  AgentCreationLogStats,
  RestartAgentCreationPayload,
} from "@/types/agent-creation-logs";

export class AgentCreationLogsService {
  static async getAgentCreationLogs(
    filters?: AgentCreationLogFilters,
    pageSize: number = 50,
    offset: number = 0
  ): Promise<AgentCreationLogsResponse> {
    try {
      let query = supabase
        .from("agent_creation_logs")
        .select("*, profiles!inner(email, full_name)", { count: "exact" })
        .order("created_at", { ascending: false });

      if (filters?.search) {
        query = query.or(`agent_name.ilike.%${filters.search}%`);
      }

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }

      if (filters?.user_id) {
        query = query.eq("user_id", filters.user_id);
      }

      if (filters?.creation_source) {
        query = query.eq("creation_source", filters.creation_source);
      }

      if (filters?.date_from) {
        query = query.gte("created_at", filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte("created_at", filters.date_to);
      }

      const { data, count, error } = await query.range(offset, offset + pageSize - 1);

      if (error) {
        throw new Error(`Failed to fetch agent creation logs: ${error.message}`);
      }

      const mappedData = (data || []).map((log: any) => ({
        ...log,
        user_email: log.profiles?.email || null,
        user_full_name: log.profiles?.full_name || null,
      }));

      return {
        data: mappedData,
        count: count || 0,
      };
    } catch (error: any) {
      console.error("Error fetching agent creation logs:", error);
      throw error;
    }
  }

  static async getAgentCreationLog(id: number): Promise<AgentCreationLog> {
    try {
      const { data, error } = await supabase
        .from("agent_creation_logs")
        .select("*, profiles!inner(email, full_name)")
        .eq("id", id)
        .single();

      if (error) {
        throw new Error(`Failed to fetch agent creation log: ${error.message}`);
      }

      return {
        ...data,
        user_email: data.profiles?.email || null,
        user_full_name: data.profiles?.full_name || null,
      };
    } catch (error: any) {
      console.error("Error fetching agent creation log:", error);
      throw error;
    }
  }

  static async getAgentCreationLogDetails(logId: number): Promise<AgentCreationLogDetailsResponse> {
    try {
      const { data, error } = await supabase
        .from("agent_creation_log_details")
        .select("*")
        .eq("log_id", logId)
        .order("started_at", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch agent creation log details: ${error.message}`);
      }

      return {
        data: data || [],
      };
    } catch (error: any) {
      console.error("Error fetching agent creation log details:", error);
      throw error;
    }
  }

  static async getAgentCreationLogStats(filters?: AgentCreationLogFilters): Promise<AgentCreationLogStats> {
    try {
      let query = supabase.from("agent_creation_logs").select("status", { count: "exact" });

      if (filters?.user_id) {
        query = query.eq("user_id", filters.user_id);
      }

      if (filters?.creation_source) {
        query = query.eq("creation_source", filters.creation_source);
      }

      const { data: allLogs, count: totalCount, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      const stats: AgentCreationLogStats = {
        total: totalCount || 0,
        completed: 0,
        failed: 0,
        in_progress: 0,
      };

      allLogs?.forEach((log: any) => {
        if (log.status === "completed") {
          stats.completed++;
        } else if (log.status === "failed" || log.status === "partial_success") {
          stats.failed++;
        } else if (log.status === "in_progress" || log.status === "pending") {
          stats.in_progress++;
        }
      });

      return stats;
    } catch (error: any) {
      console.error("Error fetching agent creation log stats:", error);
      throw error;
    }
  }

  static async restartAgentCreation(payload: RestartAgentCreationPayload): Promise<any> {
    try {
      const token = await TokenService.getAccessToken();

      if (!token) {
        throw new Error("Authentication required for agent restart");
      }

      const { data, error } = await supabase.functions.invoke("handle-agent", {
        body: {
          action: "retry-create",
          original_log_id: payload.original_log_id,
          data: payload.edited_params,
          retry_reason: payload.retry_reason,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to restart agent creation");
      }

      if (!data?.success) {
        throw new Error(data?.message || "Failed to restart agent creation");
      }

      return data;
    } catch (error: any) {
      console.error("Error restarting agent creation:", error);
      throw error;
    }
  }

  static async exportToCSV(filters?: AgentCreationLogFilters): Promise<string> {
    try {
      let query = supabase.from("agent_creation_logs").select("*");

      if (filters?.search) {
        query = query.or(`agent_name.ilike.%${filters.search}%`);
      }

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }

      if (filters?.user_id) {
        query = query.eq("user_id", filters.user_id);
      }

      if (filters?.creation_source) {
        query = query.eq("creation_source", filters.creation_source);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to export logs: ${error.message}`);
      }

      if (!data || data.length === 0) {
        return "genie_name,status,creation_source,error_message,retry_count,created_at,completed_at\n";
      }

      const headers = ["genie_name", "status", "creation_source", "error_message", "retry_count", "created_at", "completed_at"];
      const rows = data.map((log) =>
        headers.map((header) => {
          const value = log[header === "genie_name" ? "agent_name" : header as keyof AgentCreationLog];
          const stringValue = String(value || "");
          return `"${stringValue.replace(/"/g, '""')}"`;
        }).join(",")
      );

      return `${headers.join(",")}\n${rows.join("\n")}`;
    } catch (error: any) {
      console.error("Error exporting agent creation logs:", error);
      throw error;
    }
  }
}
